import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

interface FeaturedImageUploadProps {
  onFileSelect: (file: File | null) => void;
  currentImageUrl?: string | undefined;
  error?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export const FeaturedImageUpload: React.FC<FeaturedImageUploadProps> = ({
  onFileSelect,
  currentImageUrl,
  error,
  disabled = false,
  className = ''
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImageUrl || null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeMb = 5;
  const maxSize = maxSizeMb * 1024 * 1024;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      setValidationError('Alleen JPG, PNG of WebP toegestaan');
      onFileSelect(null);
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      setValidationError(`Afbeelding te groot (max ${maxSizeMb}MB)`);
      onFileSelect(null);
      return;
    }

    // Create preview URL
    setValidationError(null);
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
    onFileSelect(file);
  }, [onFileSelect, maxSize]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  }, [disabled, handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleBrowseClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleRemoveImage = useCallback(() => {
    setPreview(null);
    setValidationError(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFileSelect]);

  // The parent may pass its own validation error (e.g. "required"); merge it
  // with the error this component detects itself (wrong type/too large) so
  // both surfaces stay in sync instead of silently swallowing the failure.
  const displayError = error || validationError;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          sf-dropzone relative overflow-hidden transition-all duration-200
          ${dragActive
            ? '!border-neon-pink/50 bg-neon-pink/[.06]'
            : displayError
              ? '!border-neon-pink/50 bg-neon-pink/[.04]'
              : 'hover:border-white/30 hover:bg-white/[.03]'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        {preview ? (
          // Preview State
          <div className="relative aspect-[4/2] rounded-item overflow-hidden">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-ink/0 hover:bg-ink/40 transition-colors duration-200 flex items-center justify-center">
              <div className="opacity-0 hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBrowseClick();
                  }}
                  className="bg-surface/90 hover:bg-surface border border-white/10 text-fg-soft p-2 rounded-full shadow-floating transition-colors"
                  disabled={disabled}
                >
                  <Upload className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  className="bg-surface/90 hover:bg-surface border border-white/10 text-neon-pink-soft p-2 rounded-full shadow-floating transition-colors"
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Empty State
          <div className="aspect-[4/2] flex flex-col items-center justify-center space-y-3 p-6">
            <div className={`
              p-3 rounded-full
              ${dragActive ? 'bg-neon-gradient text-ink' : 'bg-white/[.06] text-fg-faint'}
              transition-colors duration-200
            `}>
              <ImageIcon className="h-8 w-8" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-fg-soft">
                {dragActive ? 'Laat de afbeelding los...' : 'Uitgelichte afbeelding uploaden'}
              </p>
              <p className="text-xs text-fg-muted mt-1">
                Sleep een afbeelding hierheen of klik om te bladeren
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[.14em] text-fg-meta mt-2">
                PNG, JPG, WebP tot 5MB • 4:2 aspectverhouding aanbevolen
              </p>
            </div>
          </div>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileInputChange}
          className="sr-only"
          disabled={disabled}
        />
      </div>

      {/* Error Message */}
      {displayError && (
        <p className="text-sm text-neon-pink-soft flex items-center space-x-1">
          <X className="h-4 w-4" />
          <span>{displayError}</span>
        </p>
      )}

    </div>
  );
};

export default FeaturedImageUpload;